import { useLazyQuery } from '@apollo/client';
import { produce } from 'immer';
import { useCallback } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { cacheUtils } from '@eluve/apollo-client';
import { useToast } from '@eluve/components';

import {
  getUtteranceAndFragmentsQuery,
  segmentUtterancesFragment,
} from './operations';

export const useUtteranceOperations = ({
  tenantId,
  appointmentId,
  segmentId,
}: {
  tenantId: string;
  appointmentId: string;
  segmentId: string;
}) => {
  const toast = useToast();
  const client = useApiClient();
  const [getUtteranceAndFragments] = useLazyQuery(
    getUtteranceAndFragmentsQuery,
  );

  const resetUtterance = useCallback(
    async (utteranceId: string) => {
      const response = await client.reviewer.resetUtterance({
        params: {
          tenantId,
          appointmentId,
          utteranceId,
        },
        body: {},
      });
      if (response.status === 201) {
        const { newUtteranceId } = response.body;
        if (newUtteranceId !== utteranceId) {
          return;
        }
        const result = await getUtteranceAndFragments({
          variables: {
            tenantId,
            ids: [newUtteranceId],
          },
        });
        // we need to add the new utterance to the list of utterances in Apollo
        cacheUtils.updateFragment(
          {
            fragment: segmentUtterancesFragment,
            key: { id: segmentId },
          },
          (existing) => {
            if (!existing) {
              return existing;
            }

            return produce(existing, (draft) => {
              const utterance = draft.utterances.find(
                (u) => u.id === utteranceId,
              );
              if (!utterance || !result.data?.utterances[0]) {
                return;
              }
              utterance.text = result.data?.utterances[0].text;
              utterance.hasHumanUpdatedText = false;
            });
          },
        );
      } else {
        toast.error('Failed to reset');
      }
    },
    [
      client,
      tenantId,
      appointmentId,
      segmentId,
      getUtteranceAndFragments,
      toast,
    ],
  );

  const mergeUtterance = useCallback(
    async (utteranceId: string) => {
      const response = await client.reviewer.mergeUtterances({
        params: {
          tenantId,
          appointmentId,
          utteranceId,
        },
        body: {},
      });
      const oldUtteranceId = utteranceId;
      if (response.status === 201) {
        const { newUtteranceId } = response.body;
        if (newUtteranceId === oldUtteranceId) {
          // There was nothing to merge according to the backend
          return;
        }

        const result = await getUtteranceAndFragments({
          variables: {
            tenantId,
            ids: [newUtteranceId],
          },
        });
        // we need to add the remove the old utterance from Apollo
        // and replace the new one with the merged data
        cacheUtils.updateFragment(
          {
            fragment: segmentUtterancesFragment,
            key: { id: segmentId },
          },
          (existing) => {
            // find the index of the split from utterance
            if (!existing) {
              return existing;
            }

            return produce(existing, (draft) => {
              const idx = draft.utterances.findIndex(
                (u) => u.id === oldUtteranceId,
              );
              if (idx >= 0) {
                draft.utterances.splice(idx, 1);
              }
              const newUtteranceIdx = draft.utterances.findIndex(
                (u) => u.id === newUtteranceId,
              );
              const newUtterance = result.data?.utterances.find(
                (u) => u.id === newUtteranceId,
              );
              if (newUtteranceIdx >= 0 && newUtterance) {
                draft.utterances[newUtteranceIdx] = newUtterance;
              }
            });
          },
        );
      } else {
        toast.error('Failed to merge');
      }
    },
    [
      client,
      tenantId,
      appointmentId,
      segmentId,
      getUtteranceAndFragments,
      toast,
    ],
  );

  const splitUtterance = useCallback(
    async ({
      utteranceId,
      fragmentId,
    }: {
      utteranceId: string;
      fragmentId: string;
    }) => {
      const response = await client.reviewer.splitUtterance({
        params: {
          tenantId,
          appointmentId,
          utteranceId,
        },
        body: {
          fragmentId,
        },
      });

      if (response.status === 201) {
        const { newUtteranceId } = response.body;
        if (newUtteranceId === utteranceId) {
          // There was nothing to split according to the backend
          return;
        }

        const result = await getUtteranceAndFragments({
          variables: {
            tenantId,
            ids: [newUtteranceId, utteranceId],
          },
        });

        // we need to add the new utterance to the list of utterances in Apollo
        cacheUtils.updateFragment(
          {
            fragment: segmentUtterancesFragment,
            key: { id: segmentId },
          },
          (existing) => {
            // find the index of the split from utterance
            // and insert the new one immediately after
            if (!existing) {
              return existing;
            }

            return produce(existing, (draft) => {
              const idx = draft.utterances.findIndex(
                (u) => u.id === utteranceId,
              );

              if (idx === -1) {
                return;
              }

              const oldUtterance = draft.utterances[idx];
              draft.utterances.splice(idx, 1, oldUtterance!);

              const newUtterance = result.data?.utterances.find(
                (u) => u.id === newUtteranceId,
              );

              if (!newUtterance) {
                return;
              }

              draft.utterances.splice(idx + 1, 0, newUtterance);
            });
          },
        );
      } else {
        toast.error('Failed to split');
      }
    },
    [
      client,
      tenantId,
      appointmentId,
      segmentId,
      getUtteranceAndFragments,
      toast,
    ],
  );

  return {
    resetUtterance,
    mergeUtterance,
    splitUtterance,
  };
};
